import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, MessageSquare, RefreshCw, Trash2, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { PHONE_TEL, PHONE_SMS } from "@/lib/content";

const TOKEN_KEY = "mmd_owner_token";

const STATUS_STYLES = {
  new: "bg-[#FEF3C7] text-[#B45309]",
  confirmed: "bg-[#DBEAFE] text-[#1D4ED8]",
  completed: "bg-[#D1FAE5] text-[#047857]",
};

export default function OwnerPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [pin, setPin] = useState("");
  const [loginError, setLoginError] = useState("");
  const [bookings, setBookings] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadBookings = useCallback(async () => {
    try {
      const { data } = await api.get("/bookings", { headers: authHeaders });
      setBookings(data);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        toast.error("Could not load bookings.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token) loadBookings();
  }, [token, loadBookings]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const { data } = await api.post("/auth/owner-login", { pin });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPin("");
    } catch (err) {
      setLoginError("Incorrect PIN. Try again.");
    }
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/bookings/${id}`, { status }, { headers: authHeaders });
      setBookings((bs) => bs.map((b) => (b.id === id ? data : b)));
    } catch {
      toast.error("Could not update booking.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/bookings/${id}`, { headers: authHeaders });
      setBookings((bs) => bs.filter((b) => b.id !== id));
    } catch {
      toast.error("Could not delete booking.");
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF9F5] px-5">
        <form
          onSubmit={handleLogin}
          className="w-full max-w-sm rounded-3xl border border-[#E5E7EB] bg-white p-10"
          data-testid="owner-login-form"
        >
          <p className="font-mono-alt text-[10px] font-semibold tracking-[0.25em] text-[#B45309]">OWNER PORTAL</p>
          <h1 className="font-display mt-3 text-2xl font-bold tracking-tight text-[#111827]">Enter your PIN</h1>
          <input
            data-testid="owner-pin-input"
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="Owner PIN"
            className="mt-6 h-12 w-full rounded-xl border border-[#E5E7EB] px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#D97706]"
          />
          {loginError && (
            <p data-testid="owner-login-error" className="mt-3 text-sm text-red-600">{loginError}</p>
          )}
          <button
            data-testid="owner-login-button"
            type="submit"
            className="mt-6 w-full rounded-full bg-[#131519] py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#D97706]"
          >
            View bookings
          </button>
          <Link to="/" className="mt-5 flex items-center justify-center gap-1 text-xs text-[#9CA3AF] hover:text-[#131519]">
            <ArrowLeft size={12} /> Back to site
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F5] px-5 py-12">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono-alt text-[10px] font-semibold tracking-[0.25em] text-[#B45309]">OWNER PORTAL</p>
            <h1 className="font-display mt-2 text-3xl font-bold tracking-tight text-[#111827]">Bookings</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="owner-refresh-button"
              onClick={loadBookings}
              className="flex items-center gap-2 rounded-full border border-[#E5E7EB] bg-white px-4 py-2 text-xs font-semibold text-[#374151] hover:border-[#D97706]"
            >
              <RefreshCw size={13} /> Refresh
            </button>
            <button
              data-testid="owner-logout-button"
              onClick={() => { sessionStorage.removeItem(TOKEN_KEY); setToken(null); }}
              className="rounded-full bg-[#131519] px-4 py-2 text-xs font-semibold text-white hover:bg-[#D97706]"
            >
              Log out
            </button>
          </div>
        </div>

        <Link to="/" data-testid="owner-back-link" className="mt-6 inline-flex items-center gap-1 text-xs text-[#9CA3AF] hover:text-[#131519]">
          <ArrowLeft size={12} /> Back to site
        </Link>

        <div className="mt-8 space-y-4" data-testid="owner-booking-list">
          {bookings === null && <p className="text-sm text-[#6B7280]">Loading…</p>}
          {bookings?.length === 0 && (
            <p className="rounded-2xl border border-dashed border-[#E5E7EB] bg-white p-10 text-center text-sm text-[#6B7280]">
              No bookings yet. New requests from the website will appear here.
            </p>
          )}
          {bookings?.map((b) => (
            <div key={b.id} data-testid="owner-booking-card" className="rounded-2xl border border-[#E5E7EB] bg-white p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-display text-lg font-semibold text-[#111827]">{b.name}</p>
                  <p className="mt-0.5 text-sm text-[#6B7280]">{b.vehicle}</p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLES[b.status]}`}>
                  {b.status.toUpperCase()}
                </span>
              </div>
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#4B5563]">
                <span className="font-semibold text-[#B45309]">
                  {b.package === "premium" ? "Premium Clean — $80" : "Basic Clean — $60"}
                </span>
                {b.preferred_time && <span>When: {b.preferred_time}</span>}
                <span className="text-[#9CA3AF]">{new Date(b.created_at).toLocaleString()}</span>
              </div>
              {b.notes && <p className="mt-3 rounded-xl bg-[#FAF9F5] p-3 text-sm text-[#4B5563]">{b.notes}</p>}
              <div className="mt-5 flex flex-wrap items-center gap-2">
                <a href={`tel:${b.phone}`} data-testid={`owner-call-${b.id}`}
                  className="flex items-center gap-1.5 rounded-full bg-[#131519] px-4 py-2 text-xs font-semibold text-white hover:bg-[#D97706]">
                  <Phone size={12} /> {b.phone}
                </a>
                <a href={`sms:${b.phone}`} data-testid={`owner-text-${b.id}`}
                  className="flex items-center gap-1.5 rounded-full border border-[#E5E7EB] px-4 py-2 text-xs font-semibold text-[#374151] hover:border-[#D97706]">
                  <MessageSquare size={12} /> Text
                </a>
                {b.status === "new" && (
                  <button data-testid={`owner-confirm-${b.id}`} onClick={() => setStatus(b.id, "confirmed")}
                    className="rounded-full bg-[#DBEAFE] px-4 py-2 text-xs font-semibold text-[#1D4ED8]">
                    Mark confirmed
                  </button>
                )}
                {b.status !== "completed" && (
                  <button data-testid={`owner-complete-${b.id}`} onClick={() => setStatus(b.id, "completed")}
                    className="rounded-full bg-[#D1FAE5] px-4 py-2 text-xs font-semibold text-[#047857]">
                    Mark completed
                  </button>
                )}
                <button data-testid={`owner-delete-${b.id}`} onClick={() => remove(b.id)}
                  className="ml-auto flex items-center gap-1 rounded-full px-3 py-2 text-xs font-semibold text-[#9CA3AF] hover:text-red-600">
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
